import { axiosAPI } from './API';
import { AffectedDevicesWarning } from 'enums';
import { IDevice, IDeviceServer, IFilter, IFilterFromServer, IRollout, IRolloutFromServer } from 'interfaces';
import { UpdatesService } from 'services';
import { DevicesService } from './DevicesService';

export interface IValidateOptions {
    ignoreIndividualFilters?: boolean,
    ignoreGlobalFilters?: boolean,
}

interface IGetAffectedRequest {
    includeDevices?: boolean,
    includeFilters?: boolean,
    manufacturerId: number,
    modelIds: number[],
    rollout: IRolloutFromServer,
}

export interface IGetAffectedWarning {
    type: AffectedDevicesWarning,
    deviceDuplicates?: Array<{
        serialNumber: string,
        thingIDs: string[],
    }>
}

export interface IGetAffectedResult {
    devices?: IDevice[],
    devicesCount: number,
    err?: string,
    locationsCount: number,
    warnings?: IGetAffectedWarning[],
}

type IGetAffectedResultServer = Modify<IGetAffectedResult, {
    devices?: IDeviceServer[],
}>

export interface IGetAffectedResponse {
    combined: IGetAffectedResult,
    rollout: {
	    global: {
            id: number,
            result: IGetAffectedResult,
            filters: {
                [filterId: number]: IGetAffectedResult,
            },
        },
        stages: {
            id: number,
            result: IGetAffectedResult,
            filters: {
                [filterId: number]: IGetAffectedResult,
            },
        }[],
    },
}

interface IGetAffectedResponseServer {
    combined: IGetAffectedResultServer,
    rollout: {
        global: {
            id: number,
            result: IGetAffectedResultServer,
            filters: {
                [filterId: number]: IGetAffectedResultServer,
            },
        },
        stages: {
            id: number,
            result: IGetAffectedResultServer,
            filters: {
                [filterId: number]: IGetAffectedResultServer,
            },
        }[],
    },
}

// Singleton for interacting with filters
class _FiltersService {
    // We're exposing validation regexes so that the bulk data modal can use them.
    // 5 digit number
    public regexLocation = /^\d{5}$/;
    // Any character that's not whitespace, 128 char max.
    // The actual spec is more strict, but I don't think we really care here.
    public regexSerial = /^[\S]{1,128}$/;

    public regexThingIDs = /^[\S]{1,256}$/;

    public getAffected(manufacturerId: number, modelIds: number[], rollout: IRollout, includeDevices = false): Promise<IGetAffectedResponse> {
        // Browsers don't support GET with a body, so this is a POST.
        const body: IGetAffectedRequest = {
            includeDevices: includeDevices,
            includeFilters: true,
            manufacturerId: manufacturerId,
            modelIds: modelIds,
            rollout: UpdatesService.mapRolloutToServer(rollout),
        }
        const p = axiosAPI.post<IGetAffectedResponseServer>(`/affectedDevices`, body)
            .then(response => {
                return this.mapAffectedFromServer(response.data);
            });
            
        p.catch((err) => {
            console.error(err);
        })
        return p;
    }

    public mapFilterFromServer = (f: IFilterFromServer): IFilter => {
        return {
            ...f,
            lastSeenTime: f.lastSeenTime ? new Date(f.lastSeenTime) : undefined,
        };
    }

    public mapFilterToServer = (f: IFilter): IFilterFromServer => {
        return {
            ...f,
            lastSeenTime: f.lastSeenTime ? f.lastSeenTime.toISOString() : undefined,
        };
    }

    private mapResultFromServer = (result: IGetAffectedResultServer): IGetAffectedResult => {
        if (!result) {
            result = {
                devices: [],
                devicesCount: 0,
                locationsCount: 0,
            }
        }

        return {
            ...result,
            devices: (result.devices || []).map(DevicesService.mapDeviceFromServer),
        }
    }

    public mapAffectedFromServer = (r: IGetAffectedResponseServer): IGetAffectedResponse => {
        const mapFilters = (filterResults: {[filterId: string]: IGetAffectedResultServer}): {[filterId: string]: IGetAffectedResult} => {
            if (!filterResults) {
                filterResults = {};
            }

            const out: {[filterId: string]: IGetAffectedResult} = {};
            for (const [filterId, result] of Object.entries(filterResults)) {
                out[filterId] = this.mapResultFromServer(result);
            }
            return out;
        }

        // Convert all nested results, including filters.
        // We're also being cautious and not assuming all fields are populated from the server.
        return {
            combined: this.mapResultFromServer(r.combined),
            rollout: {
                global: {
                    id: r.rollout?.global?.id,
                    result: this.mapResultFromServer(r.rollout?.global?.result),
                    filters: mapFilters(r.rollout?.global?.filters)
                },
                stages: (r.rollout?.stages || []).map(stage => ({
                    id: stage?.id,
                    result: this.mapResultFromServer(stage?.result),
                    filters: mapFilters(stage?.filters),
                })),
            },
        }
    }
}

export const FiltersService = new _FiltersService();
